import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trees, Zap, Wind, Bus, ChevronRight, Utensils, Droplets, TreePine } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/telemetry";

/**
 * BasecampsCarousel — horizontal-scroll strip on the Discover home page.
 * Sorted by proximity when user coords are provided; otherwise shows a
 * plain UK-wide list (limit 12).
 *
 * Fails silently if the endpoint is empty or 404s so the Discover page
 * never breaks because a basecamp collection is missing.
 */
export default function BasecampsCarousel({ coords }) {
  const qc = useQueryClient();
  // "Perfect lunch stop" narrows to basecamps that would actually work as a
  // mid-walk food/rest stop — any of on-site cafe, nearby pub, or picnic
  // tables. Server-side filter so we don't ship a stale full list.
  const [lunchOnly, setLunchOnly] = useState(false);
  // Universal amenity toggles — keys match the /basecamps query params.
  const [amenities, setAmenities] = useState([]);
  // Session flag consumed by BasecampDetail's `viewed_basecamp` and by
  // TransitSection's `basecamp_transit_planned` — set whenever the walker
  // has signalled lunch intent via the "Perfect lunch stop" chip OR the
  // Water Tap / Picnic Area amenity chips.
  const syncLunchIntent = (lunch, amens) => {
    const intent = lunch || amens.includes("picnic_area") || amens.includes("water_tap");
    try {
      if (intent) sessionStorage.setItem("td_lunch_intent", "1");
      else sessionStorage.removeItem("td_lunch_intent");
    } catch { /* ignore */ }
  };
  const toggleAmenity = (key) => {
    setAmenities((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Only log applications (not removals) — same rule as the lunch chip.
      if (!prev.includes(key)) track("filter_change", { filter: "amenity", amenity: key, surface: "basecamps" });
      syncLunchIntent(lunchOnly, next);
      return next;
    });
  };
  const q = useQuery({
    queryKey: ["basecamps-nearby", coords?.lat, coords?.lng, lunchOnly, amenities],
    queryFn: async () => {
      const params = { limit: 12 };
      if (coords?.lat && coords?.lng) {
        params.lat = coords.lat;
        params.lng = coords.lng;
        // Wide enough to cover all of GB from any corner — the sort still
        // brings the closest picks to the front.
        params.radius_km = 500;
      }
      if (lunchOnly) params.lunch_stop = true;
      if (amenities.includes("shaded")) params.shaded = true;
      if (amenities.includes("water_tap")) params.water_tap = true;
      if (amenities.includes("picnic_area")) params.picnic = true;
      try {
        return (await api.get("/basecamps", { params })).data;
      } catch { return { items: [] }; }
    },
    retry: 0,
    staleTime: 60_000,
  });

  const items = q.data?.items || [];
  const anyFilterActive = lunchOnly || amenities.length > 0;
  // Only hide the carousel entirely when the base (unfiltered) view is also
  // empty. When the user narrowed with a chip we still want to show the
  // empty-state so they know the filter's active.
  if (items.length === 0 && !anyFilterActive) return null;

  return (
    <section className="mt-6 mb-6" data-testid="basecamps-carousel">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-heading text-lg text-brand-text flex items-center gap-1.5">
          <Trees size={16} className="text-brand-primary" /> Basecamps nearby
        </h2>
        <span className="text-xs text-brand-subtle">{items.length}</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <button
          data-testid="basecamps-chip-lunch"
          onClick={() => {
            setLunchOnly((prev) => {
              const next = !prev;
              // Only fire on activation — the "off" toggle isn't a walker
              // intent signal and would dilute the conversion aggregate.
              if (next) {
                track("basecamp_lunch_filter_used", {
                  has_coords: !!(coords?.lat && coords?.lng),
                });
              }
              // Session-scoped flag: read by BasecampDetail's viewed_basecamp
              // event so the admin can see which specific basecamps lunch-
              // intent walkers actually opened. Also honours active amenity
              // toggles (Water Tap / Picnic Area) so intent isn't lost when
              // this chip toggles off but an amenity chip is still on.
              syncLunchIntent(next, amenities);
              return next;
            });
          }}
          aria-pressed={lunchOnly}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            lunchOnly
              ? "bg-brand-primary text-brand-sand border-brand-primary"
              : "bg-brand-sand text-brand-text border-brand-border hover:border-brand-primary/40"
          }`}
        >
          <Utensils size={11} /> Perfect lunch stop
        </button>
        {[
          { key: "shaded", label: "Shaded", Icon: TreePine },
          { key: "water_tap", label: "Water Tap", Icon: Droplets },
          { key: "picnic_area", label: "Picnic Area", Icon: Utensils },
        ].map(({ key, label, Icon }) => {
          const on = amenities.includes(key);
          return (
            <button
              key={key}
              data-testid={`basecamps-chip-${key}`}
              onClick={() => toggleAmenity(key)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                on
                  ? "bg-brand-primary text-brand-sand border-brand-primary"
                  : "bg-brand-sand text-brand-text border-brand-border hover:border-brand-primary/40"
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div
          className="td-card p-4 text-sm text-brand-subtle"
          data-testid="basecamps-empty-lunch"
        >
          No basecamps near you match the active filters yet. Tap the chips again to see all basecamps.
        </div>
      ) : (
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4">
        {items.map((b) => {
          const isHigh = b.energy_match === "high_energy";
          const distKm = typeof b.distance_km === "number" ? b.distance_km.toFixed(1) : null;
          const busKm = typeof b.bus_distance_meters === "number" ? (b.bus_distance_meters / 1000).toFixed(1) : null;
          return (
            <Link
              key={b.basecamp_id}
              to={`/basecamp/${b.basecamp_id}`}
              data-testid={`basecamp-card-${b.basecamp_id}`}
              // Warm the detail-page cache the first time the card is in
              // range of a tap — hover on desktop, first render on mobile —
              // so the /basecamp/:id route pops instantly.
              onMouseEnter={() => qc.prefetchQuery({
                queryKey: ["basecamp", b.basecamp_id],
                queryFn: async () => (await api.get(`/basecamps/${b.basecamp_id}`)).data,
                staleTime: 60_000,
              })}
              onTouchStart={() => qc.prefetchQuery({
                queryKey: ["basecamp", b.basecamp_id],
                queryFn: async () => (await api.get(`/basecamps/${b.basecamp_id}`)).data,
                staleTime: 60_000,
              })}
              className="shrink-0 w-52 td-card p-3 snap-start hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    isHigh
                      ? "bg-brand-error/10 text-brand-error border-brand-error/30"
                      : "bg-brand-primary/10 text-brand-primary border-brand-primary/30"
                  }`}
                >
                  {isHigh ? <Zap size={9} /> : <Wind size={9} />}
                  {isHigh ? "High energy" : "Chill"}
                </span>
                {distKm && (
                  <span className="text-[10px] text-brand-subtle">{distKm} km</span>
                )}
              </div>
              <div className="font-heading text-sm text-brand-text leading-snug line-clamp-2 min-h-[36px]">
                {b.name}
              </div>
              <div className="text-[11px] text-brand-subtle mt-1.5 flex items-center gap-1">
                <Bus size={10} />
                {busKm ? `${busKm} km to bus` : "Transit info coming soon"}
              </div>
              <div className="text-[11px] text-brand-primary mt-2 flex items-center gap-0.5">
                Open <ChevronRight size={11} />
              </div>
            </Link>
          );
        })}
      </div>
      )}
    </section>
  );
}
